import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query'
import { requestV3 } from 'Features/Auth/axios-client'
import { ReplenishmentSiteSku } from 'Features/Projects/Scenarios/EditSettings/NetworkSettings/Replenishment/Replenishment.types'

const fetchReplenishmentSiteSkus = async (
  projectId: string,
  scenarioId: string
) => {
  const { data } = await requestV3({
    url: `/replenishment-site-skus?projectId=${projectId}&scenarioId=${scenarioId}`,
  })
  return data
}

export const useReplenishmentSiteSkus = ({
  projectId,
  scenarioId,
}: {
  projectId: string
  scenarioId: string
}): UseQueryResult<ReplenishmentSiteSku[]> => {
  return useQuery({
    queryKey: ['replenishment-site-skus', projectId, scenarioId],
    queryFn: () => fetchReplenishmentSiteSkus(projectId, scenarioId),
    select: (data) => data.data,
    refetchOnWindowFocus: false,
    gcTime: Infinity,
  })
}

// MUTATION TO UPDATE REPLENISHMENT SITE SKU DATA
export interface UpdateReplenishmentInterface {
  changedRows: Record<string, ReplenishmentSiteSku>
  projectId: string
  scenarioId: string
}

const updateReplenishmentSiteSkus = async ({
  changedRows,
  projectId,
  scenarioId,
}: UpdateReplenishmentInterface) => {
  const { data } = await requestV3({
    url: `/replenishment-site-skus?projectId=${encodeURIComponent(
      projectId
    )}&scenarioId=${encodeURIComponent(scenarioId)}`,
    method: 'PUT',
    data: changedRows,
  })
  return data
}

export const useUpdateReplenishmentSiteSkus = (
  onSuccess: () => void,
  onError: () => void
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: updateReplenishmentSiteSkus,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [
          'replenishment-site-skus',
          variables.projectId,
          variables.scenarioId,
        ],
      })
      onSuccess()
    },
    onError,
  })
}

//  "/api/v3/replenishment-cycle-time/project/<project_id>/scenario/<scenario_id>/workcentre/<workcentre_code>", write for this new API.

type ReplenishmentCycleTime = {
  projectId: string
  scenarioId: string
}
const fetchReplenishmentCycleTime = async (
  cycleTimeArgs: ReplenishmentCycleTime
) => {
  const { data } = await requestV3({
    url: `/replenishment-cycle-time/project/${cycleTimeArgs.projectId}/scenario/${cycleTimeArgs.scenarioId}/`,
  })
  return data
}

const uploadReplenishmentSiteSkus = async ({
  file,
  projectId,
  scenarioId,
}: {
  file: File
  projectId: string
  scenarioId: string
}) => {
  const formData = new FormData()
  formData.append('file', file)
  const { data } = await requestV3({
    url: `/replenishment-site-skus?projectId=${encodeURIComponent(projectId)}&scenarioId=${encodeURIComponent(scenarioId)}`,
    method: 'POST',
    data: formData,
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data
}

export const useUploadReplenishmentSiteSkus = (
  onSuccess: () => void,
  onError: () => void
) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: uploadReplenishmentSiteSkus,
    onSuccess: (_, { projectId, scenarioId }) => {
      queryClient.invalidateQueries({
        queryKey: ['replenishment-site-skus', projectId, scenarioId],
      })
      onSuccess()
    },
    onError,
  })
}

export const useReplenishmentCycleTime = (
  cycleTimeArgs: ReplenishmentCycleTime
) => {
  return useQuery({
    queryKey: [
      'replenishment-cycle-time',
      cycleTimeArgs.projectId,
      cycleTimeArgs.scenarioId,
    ],
    queryFn: () => fetchReplenishmentCycleTime(cycleTimeArgs),
    select: (data) => data.data,
    refetchOnWindowFocus: false,
    gcTime: Infinity,
  })
}
